// src/hooks/useTokenExpiry.js
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, checkTokenExpiry, refreshTokenExpiry } from '../redux/Slices/authSlice';

export const useTokenExpiry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, tokenExpiry, isAuthenticated } = useSelector((state) => state.auth);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    dispatch(checkTokenExpiry());
  }, [dispatch]);

  useEffect(() => {
    if (!token || !tokenExpiry || !isAuthenticated) {
      setTimeRemaining(null);
      return;
    }

    const checkExpiry = () => {
      const now = Date.now();
      const remaining = tokenExpiry - now;

      if (remaining <= 0) {
        dispatch(logout());
        navigate('/');
        return;
      }

      setTimeRemaining(remaining);
    };

    checkExpiry();

    const interval = setInterval(checkExpiry, 60000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, tokenExpiry, isAuthenticated, dispatch, navigate]);

  const refreshExpiry = () => {
    dispatch(refreshTokenExpiry());
  };

  const formatTimeRemaining = () => {
    if (!timeRemaining) return null;
    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  };

  return {
    timeRemaining,
    formatTimeRemaining,
    isExpired: timeRemaining !== null && timeRemaining <= 0,
    refreshExpiry,
  };
};